/* eslint-disable react/jsx-props-no-spreading */
import {
  Autocomplete,
  AutocompleteProps,
  AutocompleteRenderInputParams,
  FormHelperText,
  Stack,
  TextField,
  TextFieldProps,
} from '@mui/material';
import { isEmpty } from 'lodash-es';
import React, { useEffect } from 'react';
import { useController, useFormContext } from 'react-hook-form';

export interface AutocompleteArrayProps<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined
> extends Omit<AutocompleteProps<T | string, Multiple, DisableClearable, FreeSolo>, 'renderInput'> {
  name: string;
  label: string;
  options: any[];
  inputProps?: TextFieldProps;
  rules?: any;
  normalizeOutput?: Function;
  normalizeInput?: Function;
  placeholder?: string;
}

function AutocompleteArray<
  T,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined
>({
  name,
  rules,
  label,
  options,
  inputProps = null,
  normalizeInput = (input) => input || [],
  normalizeOutput = (event) => event,
  defaultValue,
  freeSolo,
  onInputChange,
  placeholder,
  ...rest
}: AutocompleteArrayProps<T, Multiple, DisableClearable, FreeSolo>) {
  const { control, resetField } = useFormContext();
  const {
    field,
    fieldState: { error },
  } = useController({ control, name, rules });

  useEffect(() => {
    if (!isEmpty(defaultValue)) {
      resetField(name, { defaultValue });
    }
  }, [defaultValue, resetField, name]);

  const value = normalizeInput(field.value, options);

  const onChange = (_: React.SyntheticEvent, newValue: (T | string)[]) => {
    field.onChange(normalizeOutput(newValue));
  };

  return (
    <Stack>
      <Autocomplete
        multiple
        size="small"
        options={options}
        value={value}
        onChange={onChange}
        onInputChange={(event, newInputValue, reason) => {
          if (onInputChange) {
            onInputChange(event, newInputValue, reason);
          }
          // Got this from https://github.com/mui/material-ui/issues/19152
          // to allow comma-limited strings to be pasted here.
          const newValuesArray = newInputValue.split(',');
          if (newValuesArray.length > 1) {
            field.onChange(
              normalizeOutput(
                value
                  .concat(newValuesArray)
                  .map((x) => x.trim())
                  .filter((x) => ((!freeSolo && options.includes(x)) || freeSolo) && x)
              )
            );
          }
        }}
        renderInput={(params: AutocompleteRenderInputParams): React.ReactNode => (
          <TextField
            label={label}
            variant="standard"
            size="small"
            name={field.name}
            placeholder={placeholder || (isEmpty(field.value) && `Add ${label || 'Value'}`)}
            onBlur={(e) => {
              if (freeSolo) {
                onChange(e, [
                  ...value,
                  ...e.target.value
                    .split(',')
                    .map((x) => x.trim())
                    .filter((x) => x),
                ]);
              }
            }}
            {...inputProps}
            {...params}
          />
        )}
        defaultValue={defaultValue}
        freeSolo={freeSolo}
        // Enabled clearOnBlur to fix issue where typed value is not cleared on blur when freeSolo is true
        // This is especially to address issues when users enter commas to separate values
        clearOnBlur={freeSolo}
        {...rest}
      />
      {error?.message && <FormHelperText error={!!error}>{error?.message ? error?.message : ` `}</FormHelperText>}
    </Stack>
  );
}

export default AutocompleteArray;
